import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Tabs, Tab, Card } from 'react-bootstrap';
import { supabase } from '../lib/supabaseClient';
import { compressImageForUpload } from '../lib/compressImage';
import ConfirmDialog from '../components/ConfirmDialog';
import './AdminDashboard.css';

const CATEGORIES = [
  { key: 'beauty', label: 'Beauty', maxImages: 8 },
  { key: 'fashion', label: 'Fashion', maxImages: 8 },
  { key: 'commercial', label: 'Commercial', maxImages: null },
];

function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [activeTab, setActiveTab] = useState('beauty');
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [uploading, setUploading] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('beauty');
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
  });

  const [formData, setFormData] = useState({
    category: 'beauty',
    type: 'image',
    src: '',
  });
  const [gdriveLink, setGdriveLink] = useState('');
  const [gdriveSaving, setGdriveSaving] = useState(false);

  useEffect(() => {
    fetchPortfolio();
    fetchGdriveLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('portfolio')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      showAlert('Error loading portfolio: ' + error.message, 'danger');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGdriveLink = async () => {
    try {
      const { data } = await supabase.from('site_settings').select('gdrive_portfolio').eq('id', 'main').maybeSingle();
      setGdriveLink(data?.gdrive_portfolio || '');
    } catch {
      setGdriveLink('');
    }
  };

  const handleSaveGdrive = async (e) => {
    e.preventDefault();
    try {
      setGdriveSaving(true);
      const { error } = await supabase
        .from('site_settings')
        .update({ gdrive_portfolio: gdriveLink.trim() || null, updated_at: new Date().toISOString() })
        .eq('id', 'main');
      if (error) throw error;
      showAlert('GDrive link tersimpan!', 'success');
    } catch (err) {
      showAlert('Error: ' + err.message, 'danger');
    } finally {
      setGdriveSaving(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const getItemsByCategory = (cat) =>
    items.filter((i) => i.category === cat);

  const getMaxForCategory = (cat) => {
    const c = CATEGORIES.find((x) => x.key === cat);
    return c?.maxImages ?? 999;
  };

  const getImageCount = (cat) =>
    getItemsByCategory(cat).filter((i) => i.type === 'image').length;

  const canAddMore = (cat) => {
    const max = getMaxForCategory(cat);
    if (!max) return true;
    return getImageCount(cat) < max;
  };

  const getSlotsLeft = (cat) => {
    const max = getMaxForCategory(cat);
    if (!max) return 999;
    return Math.max(0, max - getImageCount(cat));
  };

  const handleShowModal = (item = null) => {
    if (item) {
      setEditMode(true);
      setCurrentItem(item);
      setFormData({
        category: item.category,
        type: item.type,
        src: item.src,
      });
    } else {
      setEditMode(false);
      setCurrentItem(null);
      setFormData({
        category: activeTab,
        type: activeTab === 'commercial' ? 'video' : 'image',
        src: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = formData.type === 'video';
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];

    if (isVideo) {
      if (!validVideoTypes.includes(file.type)) {
        showAlert('Please upload MP4 or WebM video', 'warning');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        showAlert('Video size must be less than 50MB', 'warning');
        return;
      }
    } else {
      if (!validImageTypes.includes(file.type)) {
        showAlert('Please upload JPG, PNG, or WebP image', 'warning');
        return;
      }
    }

    try {
      setUploading(true);
      let fileToUpload = file;
      if (!isVideo) {
        fileToUpload = await compressImageForUpload(file);
      }
      const ext = fileToUpload.name.split('.').pop() || (isVideo ? 'mp4' : 'jpg');
      const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { error } = await supabase.storage
        .from('portfolio')
        .upload(fileName, fileToUpload, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('portfolio')
        .getPublicUrl(fileName);

      setFormData({ ...formData, src: urlData.publicUrl });
      showAlert('File uploaded!', 'success');
    } catch (error) {
      showAlert('Upload error: ' + error.message, 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.src?.trim()) {
      showAlert('Please upload a file or enter URL', 'warning');
      return;
    }

    const cat = formData.category;
    if (!editMode && (cat === 'beauty' || cat === 'fashion') && formData.type === 'image' && !canAddMore(cat)) {
      showAlert(`${cat === 'beauty' ? 'Beauty' : 'Fashion'} sudah penuh (max 8).`, 'warning');
      return;
    }

    try {
      const maxOrder = Math.max(0, ...items.filter((i) => i.category === formData.category).map((i) => i.display_order || 0));

      if (editMode) {
        const { error } = await supabase
          .from('portfolio')
          .update({
            category: formData.category,
            type: formData.type,
            src: formData.src.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentItem.id);

        if (error) throw error;
        showAlert('Portfolio item updated!', 'success');
      } else {
        const { error } = await supabase
          .from('portfolio')
          .insert({
            category: formData.category,
            type: formData.type,
            src: formData.src.trim(),
            display_order: maxOrder + 1,
          });

        if (error) throw error;
        showAlert('Portfolio item added!', 'success');
      }

      handleCloseModal();
      fetchPortfolio();
    } catch (error) {
      showAlert('Error saving: ' + error.message, 'danger');
    }
  };

  const handleDelete = (id, label) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Portfolio Item',
      message: `Are you sure you want to delete this item?`,
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('portfolio').delete().eq('id', id);
          if (error) throw error;
          showAlert('Deleted successfully!', 'success');
          fetchPortfolio();
        } catch (error) {
          showAlert('Error deleting: ' + error.message, 'danger');
        }
      },
      variant: 'danger',
    });
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const cat = bulkCategory;
    if (cat !== 'beauty' && cat !== 'fashion') {
      showAlert('Bulk upload hanya untuk Beauty & Fashion', 'warning');
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const imageFiles = files.filter((f) => validTypes.includes(f.type));
    const invalidCount = files.length - imageFiles.length;
    if (invalidCount > 0) {
      showAlert(`${invalidCount} file bukan gambar (JPG/PNG/WebP), akan dilewati`, 'warning');
    }

    const slotsLeft = getSlotsLeft(cat);
    const toUpload = imageFiles.slice(0, slotsLeft);
    if (toUpload.length === 0) {
      showAlert(`${cat === 'beauty' ? 'Beauty' : 'Fashion'} sudah penuh (max 8). Hapus dulu jika ingin ganti.`, 'warning');
      e.target.value = '';
      return;
    }
    if (imageFiles.length > slotsLeft) {
      showAlert(`Hanya ${slotsLeft} slot tersisa. Mengupload ${toUpload.length} dari ${imageFiles.length} file.`, 'info');
    }

    const catItems = getItemsByCategory(cat);
    const maxOrder = Math.max(0, ...catItems.map((i) => i.display_order || 0));

    setUploading(true);
    setBulkProgress({ current: 0, total: toUpload.length });

    let successCount = 0;
    for (let i = 0; i < toUpload.length; i++) {
      setBulkProgress({ current: i + 1, total: toUpload.length });
      const file = toUpload[i];
      try {
        const compressed = await compressImageForUpload(file);
        const ext = compressed.name.split('.').pop() || 'jpg';
        const fileName = `${Date.now()}_${i}_${Math.random().toString(36).slice(2)}.${ext}`;

        const { error: uploadErr } = await supabase.storage
          .from('portfolio')
          .upload(fileName, compressed, { cacheControl: '3600', upsert: false });

        if (uploadErr) throw uploadErr;

        const { data: urlData } = supabase.storage.from('portfolio').getPublicUrl(fileName);
        const url = urlData.publicUrl;

        const { error: insertErr } = await supabase.from('portfolio').insert({
          category: cat,
          type: 'image',
          src: url,
          display_order: maxOrder + i + 1,
        });

        if (insertErr) throw insertErr;
        successCount++;
      } catch (err) {
        showAlert(`Error pada file ${i + 1}: ${err.message}`, 'danger');
      }
    }

    setUploading(false);
    setBulkProgress({ current: 0, total: 0 });
    setShowBulkModal(false);
    e.target.value = '';
    if (successCount > 0) {
      showAlert(`${successCount} foto berhasil diupload ke ${cat === 'beauty' ? 'Beauty' : 'Fashion'}!`, 'success');
      fetchPortfolio();
    }
  };

  const handleMoveOrder = async (item, direction) => {
    const catItems = getItemsByCategory(item.category).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const idx = catItems.findIndex((i) => i.id === item.id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= catItems.length) return;

    const curr = catItems[idx];
    const swap = catItems[swapIdx];
    const currOrder = curr.display_order ?? idx;
    const swapOrder = swap.display_order ?? swapIdx;

    try {
      await supabase.from('portfolio').update({ display_order: swapOrder, updated_at: new Date().toISOString() }).eq('id', curr.id);
      await supabase.from('portfolio').update({ display_order: currOrder, updated_at: new Date().toISOString() }).eq('id', swap.id);
      showAlert('Order updated!', 'success');
      fetchPortfolio();
    } catch (error) {
      showAlert('Error updating order: ' + error.message, 'danger');
    }
  };

  return (
    <div className="admin-dashboard">
      <Container fluid className="py-4">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Portfolio Management</h1>
            <p className="admin-subtitle">Beauty & Fashion max 8 foto masing-masing. Commercial tanpa batas.</p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={() => { setBulkCategory(activeTab === 'commercial' ? 'beauty' : activeTab); setShowBulkModal(true); }}
              disabled={uploading}
            >
              <i className="bi bi-cloud-arrow-up me-2"></i>
              Bulk Upload
            </Button>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Item
            </Button>
          </div>
        </div>

        {alert.show && (
          <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
              <Tab eventKey="gdrive" title="GDrive Portfolio">
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-4">
                    <p className="text-muted mb-3">Link Google Drive untuk portfolio video. Muncul di tab &quot;GDrive Portfolio&quot; di halaman utama.</p>
                    <Form onSubmit={handleSaveGdrive}>
                      <Form.Group className="mb-3">
                        <Form.Label>Google Drive Link</Form.Label>
                        <Form.Control
                          type="url"
                          value={gdriveLink}
                          onChange={(e) => setGdriveLink(e.target.value)}
                          placeholder="https://drive.google.com/drive/folders/..."
                        />
                      </Form.Group>
                      <Button type="submit" variant="primary" disabled={gdriveSaving}>
                        {gdriveSaving ? 'Menyimpan...' : 'Simpan'}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab>
              {CATEGORIES.map((cat) => {
                const count = getImageCount(cat.key);
                const max = cat.maxImages;
                const title = max ? `${cat.label} (${count}/${max})` : `${cat.label} (${getItemsByCategory(cat.key).length})`;
                return (
                <Tab key={cat.key} eventKey={cat.key} title={title}>
                  <Table className="admin-table">
                    <thead>
                      <tr>
                        <th>Preview</th>
                        <th>Type</th>
                        <th>Order</th>
                        <th style={{ width: 140 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getItemsByCategory(cat.key).length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            No items. Click &quot;Add Item&quot; to add.
                          </td>
                        </tr>
                      ) : (
                        getItemsByCategory(cat.key)
                          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                          .map((item, idx, arr) => (
                            <tr key={item.id}>
                              <td>
                                {item.type === 'image' ? (
                                  <img src={item.src} alt="" className="model-thumb" style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover' }} />
                                ) : (
                                  <div className="model-thumb bg-dark text-white d-flex align-items-center justify-content-center" style={{ width: 80, height: 60 }}>
                                    <i className="bi bi-play-circle fs-4"></i>
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className={`badge ${item.type === 'video' ? 'badge-adult' : 'badge-kids'}`}>
                                  {item.type}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <Button size="sm" variant="outline-secondary" onClick={() => handleMoveOrder(item, 'up')} disabled={idx === 0}>
                                    <i className="bi bi-chevron-up"></i>
                                  </Button>
                                  <Button size="sm" variant="outline-secondary" onClick={() => handleMoveOrder(item, 'down')} disabled={idx === arr.length - 1}>
                                    <i className="bi bi-chevron-down"></i>
                                  </Button>
                                </div>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <Button variant="outline-primary" size="sm" onClick={() => handleShowModal(item)} title="Edit">
                                    <i className="bi bi-pencil"></i>
                                  </Button>
                                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.id)} title="Delete">
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </Table>
                </Tab>
              );})}
            </Tabs>
          </div>
        )}
      </Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? 'Edit Portfolio Item' : 'Add Portfolio Item'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select name="category" value={formData.category} onChange={handleInputChange}>
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select name="type" value={formData.type} onChange={handleInputChange}>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>File / URL</Form.Label>
              <Form.Control
                type="file"
                accept={formData.type === 'video' ? 'video/mp4,video/webm,video/quicktime' : 'image/jpeg,image/jpg,image/png,image/webp'}
                onChange={handleFileUpload}
                disabled={uploading}
                className="mb-2"
              />
              <div className="text-muted small mb-2">— or paste URL —</div>
              <Form.Control
                type="url"
                name="src"
                value={formData.src}
                onChange={handleInputChange}
                placeholder={formData.type === 'video' ? 'https://...video.mp4' : 'https://...image.jpg'}
              />
              {formData.src && (
                <div className="mt-2">
                  {formData.type === 'image' ? (
                    <img src={formData.src} alt="Preview" style={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover' }} />
                  ) : (
                    <video src={formData.src} controls style={{ maxWidth: 200 }} />
                  )}
                </div>
              )}
            </Form.Group>

            {uploading && (
              <div className="text-center mb-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                Uploading...
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
              <Button variant="primary" type="submit">Save</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal show={showBulkModal} onHide={() => !uploading && setShowBulkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-cloud-arrow-up me-2"></i>Bulk Upload Foto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Pilih banyak gambar sekaligus. Beauty & Fashion max 8 foto masing-masing.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}>
              <option value="beauty">Beauty ({getImageCount('beauty')}/8)</option>
              <option value="fashion">Fashion ({getImageCount('fashion')}/8)</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Pilih File (bisa banyak)</Form.Label>
            <Form.Control
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleBulkUpload}
              disabled={uploading || !canAddMore(bulkCategory)}
            />
            {!canAddMore(bulkCategory) && (
              <Form.Text className="text-danger d-block mt-1">
                {bulkCategory === 'beauty' ? 'Beauty' : 'Fashion'} sudah penuh (8/8). Hapus foto dulu jika ingin menambah.
              </Form.Text>
            )}
          </Form.Group>
          {uploading && (
            <div className="text-center mt-3 py-3">
              <div className="spinner-border text-primary me-2" role="status"></div>
              <span>Uploading {bulkProgress.current}/{bulkProgress.total}...</span>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ConfirmDialog
        show={confirmDialog.show}
        onHide={() => setConfirmDialog({ ...confirmDialog, show: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />
    </div>
  );
}

export default AdminPortfolio;
